import { useCallback, useEffect, useMemo, useState } from 'react';
import '@xyflow/react/dist/style.css';
import { v4 as uuidv4 } from 'uuid';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  type Node,
  type Edge,
  type Connection,
  useReactFlow,
  ReactFlowProvider,
  type NodeProps,
} from '@xyflow/react';

import {
  isAllTestGroupsSelected,
  isDuplicateItem,
  isNewNodeItem,
  NodeTypes,
  sanitizeNodes,
} from '@utils/flows';
import type { ParentNode, DefaultNodeData, NodeItem } from '@models/flows';
import type { ChargerFlow, CreateUpdateChargerFlowBody } from '@models/chargerFlows';

import { useToast } from '@hooks/useToast';
import { useNavigationGuard } from '@hooks/useNavigationGuard';
import { useSetNavigationGuardStore } from '@features/navigationGuard/store';
import { useCreateChargerFlow, useUpdateChargerFlow } from '@features/chargerFlows/mutations';

import {
  ErrorNode,
  SettingsNode,
  ConditionalNode,
  TestGroupsNode,
  FlowEditActionBar,
  TransactionNatureNode,
  RecurringActionNode,
  ValueNode,
  DebtCollectionNode,
  FailureNode,
  SuccessNode,
} from '@components/Flows/components';
import FlowEditNavBar from '@components/Flows/FlowEditNavBar';
import NodeConfigrationNavigation from '@components/Flows/components/NodeConfigrationNavigation';

import { getFlowConnectionError } from '@features/chargerFlows/validation/connection-validation';
import {
  getInitialNodeData,
  isNodeType,
  isValidNodeType,
} from '@features/chargerFlows/validation/validation-helpers';
import { validateNodesBeforeSave } from '@features/chargerFlows/validation/flows-validation';

import s from './FlowEdit.module.css';

type FlowEditProps = {
  chargerFlow: ChargerFlow;
};

type ConfigurationPanelProps = {
  isOpen: boolean;
  parentNode: ParentNode | null;
  childNode: NodeItem | null;
  handleSave: (parentNode: ParentNode, childNode: NodeItem) => void;
  handleDelete: (parentNode: ParentNode, childNode: NodeItem) => void;
  handleClose: () => void;
};

const defaultConfigState: ConfigurationPanelProps = {
  isOpen: true,
  parentNode: null,
  childNode: null,
  handleSave: () => {},
  handleDelete: () => {},
  handleClose: () => {},
};

const ReactFlowBase = ({ chargerFlow }: FlowEditProps) => {
  const [configurationPanel, setConfigurationPanel] =
    useState<ConfigurationPanelProps>(defaultConfigState);
  const toast = useToast();
  const [flowName, setFlowName] = useState<string>(chargerFlow.name || '');
  const parentNodes = (chargerFlow.content.nodes as Node[]) || [];
  const parentEdges = (chargerFlow.content.edges as Edge[]) || [];
  const [nodes, setNodes, onNodesChange] = useNodesState(parentNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(parentEdges);
  const { screenToFlowPosition, getViewport } = useReactFlow();
  const { mutate: createChargerFlow } = useCreateChargerFlow();
  const { mutate: updateChargerFlow } = useUpdateChargerFlow();
  const [error, setError] = useState<string | null>(null);
  const setNavigationGuard = useSetNavigationGuardStore();
  useNavigationGuard(); // Navigation guard hook

  // Listen to changes in flowName, nodes, edges to set navigation guard
  useEffect(() => {
    setNavigationGuard(true);
  }, [flowName, nodes, edges, setNavigationGuard]);

  // On mount, set guard to false, otherwise top hook overrides
  useEffect(() => {
    setNavigationGuard(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle displaying errors
  useEffect(() => {
    if (error && error !== '') {
      toast.error(error);
      setError(null);
    }
  }, [error, toast]);

  const onConnect = useCallback(
    (connection: Connection) => {
      const message = getFlowConnectionError(connection, nodes, edges);
      if (message) {
        toast.error(message);
        return;
      }
      setEdges((eds) => addEdge(connection, eds));
    },
    [setEdges, nodes, edges, toast],
  );

  // Function to handle adding a new node to the workspace
  const handleNewNodeToWorkspace = useCallback(
    (nodeType: string) => {
      if (!nodeType || !isValidNodeType(nodeType)) {
        setError(`Node type "${nodeType}" is not recognized.`);
        return;
      }

      const hasSettingsOrErrorNode = nodes.some((n) =>
        isNodeType(n.type as string, NodeTypes.settingsNode, NodeTypes.errorNode),
      );
      if (
        (isNodeType(nodeType, NodeTypes.settingsNode) ||
          isNodeType(nodeType, NodeTypes.errorNode)) &&
        hasSettingsOrErrorNode
      ) {
        toast.error('Only one Settings or Error Node is allowed in a flow.');
        return;
      }

      const { x, y } = getViewport();
      const center = {
        x,
        y,
      };
      const position = screenToFlowPosition(center);

      const newNode = {
        id: uuidv4(),
        type: nodeType,
        position,
        data: getInitialNodeData(nodeType),
      };

      setNodes((nodes) => nodes.concat(newNode));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setNodes, screenToFlowPosition, nodes, toast],
  );

  // Handle node data changes
  const onChange = useCallback(
    (parentNode: ParentNode, childNode: NodeItem, isDelete?: boolean) => {
      setNodes((nodes) =>
        nodes.map((node) => {
          const currentParentNode = node.id === parentNode.id && parentNode;

          if (currentParentNode) {
            const parentNodeType = currentParentNode.type as string;
            if (!isValidNodeType(parentNodeType)) {
              setError(`Node type "${parentNodeType}" is not recognized.`);
              return node;
            }

            const parentNodeData = currentParentNode.data as unknown as DefaultNodeData;
            const parentNodeItems = parentNodeData.items || [];
            if (!parentNodeItems) {
              setError('Node items data is missing or invalid.');
              return node;
            }

            // Add new children node item
            if (isNewNodeItem(currentParentNode, childNode) && !isDelete) {
              return {
                ...node,
                data: {
                  ...node.data,
                  items: [...parentNodeItems, childNode],
                },
              };
            }

            // Remove children node item if delete action
            if (isDelete) {
              const filteredItems = parentNodeItems.filter((item) => item.id !== childNode.id);
              return {
                ...node,
                data: {
                  ...node.data,
                  items: filteredItems,
                },
              };
            }

            // Check wether item already exists
            if (isDuplicateItem(currentParentNode, childNode)) {
              setError(`Node item already exists.`);
              return node;
            }

            // If 'All' selected for test groups, replace all items with the 'All' item
            if (
              isNodeType(parentNodeType, NodeTypes.testGroupsNode) &&
              isAllTestGroupsSelected(childNode.isAllSelected as boolean)
            ) {
              return {
                ...node,
                data: {
                  ...node.data,
                  items: [childNode],
                },
              };
            }

            return {
              ...node,
              data: {
                ...node.data,
                items: parentNodeItems.map((item) => (item.id === childNode.id ? childNode : item)),
              },
            };
          }

          return node;
        }),
      );
    },
    [setNodes],
  );

  const handleNodeEdit = (parentNode: ParentNode, childNode: NodeItem) => {
    if (!parentNode || !childNode) {
      setError('No node data provided for editing.');
      return;
    }
    if (!isValidNodeType(parentNode.type)) {
      setError(`Node type "${parentNode.type}" is not recognized.`);
      return;
    }

    setConfigurationPanel({
      isOpen: true,
      parentNode,
      childNode,
      handleSave: (parentNode: ParentNode, childNode: NodeItem) => {
        onChange(parentNode, childNode);
        setConfigurationPanel(defaultConfigState);
      },
      handleDelete: (parentNode: ParentNode, childNode: NodeItem) => {
        onChange(parentNode, childNode, true);
        setConfigurationPanel(defaultConfigState);
      },
      handleClose: () => {
        setConfigurationPanel(defaultConfigState);
      },
    });
  };

  const handleNodeDelete = (nodeId: string) => {
    setNodes((nodes) => nodes.filter((node) => node.id !== nodeId));
    setEdges((edges) => edges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
  };

  const nodeTypes = useMemo(
    () => ({
      errorNode: (props: NodeProps) => (
        <ErrorNode
          {...props}
          onChange={onChange}
          onEdit={handleNodeEdit}
          onDelete={handleNodeDelete}
        />
      ),
      settingsNode: (props: NodeProps) => (
        <SettingsNode
          {...props}
          onChange={onChange}
          onEdit={handleNodeEdit}
          onDelete={handleNodeDelete}
        />
      ),
      conditionalNode: (props: NodeProps) => (
        <ConditionalNode {...props} onEdit={handleNodeEdit} onDelete={handleNodeDelete} />
      ),
      testGroupsNode: (props: NodeProps) => (
        <TestGroupsNode
          {...props}
          onChange={onChange}
          onEdit={handleNodeEdit}
          onDelete={handleNodeDelete}
        />
      ),
      transactionNatureNode: (props: NodeProps) => (
        <TransactionNatureNode
          {...props}
          onChange={onChange}
          onEdit={handleNodeEdit}
          onDelete={handleNodeDelete}
        />
      ),
      recurringActionNode: (props: NodeProps) => (
        <RecurringActionNode {...props} onEdit={handleNodeEdit} onDelete={handleNodeDelete} />
      ),
      valueNode: (props: NodeProps) => (
        <ValueNode {...props} onEdit={handleNodeEdit} onDelete={handleNodeDelete} />
      ),
      debtCollectionNode: (props: NodeProps) => (
        <DebtCollectionNode {...props} onDelete={handleNodeDelete} />
      ),
      failureNode: (props: NodeProps) => (
        <FailureNode {...props} onEdit={handleNodeEdit} onDelete={handleNodeDelete} />
      ),
      successNode: (props: NodeProps) => (
        <SuccessNode {...props} onEdit={handleNodeEdit} onDelete={handleNodeDelete} />
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleSaveFlow = () => {
    const validationError = validateNodesBeforeSave(nodes, edges);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const isNewFlow = !chargerFlow.id;

    const body: CreateUpdateChargerFlowBody = {
      name: flowName,
      version: 'v1',
      content: {
        nodes: sanitizeNodes(nodes),
        edges,
      },
    };

    if (isNewFlow) {
      createChargerFlow({ chargerFlow: body });
      return;
    }

    updateChargerFlow({ flowId: chargerFlow.id, chargerFlow: body });
  };

  return (
    <div className={s.container}>
      <ReactFlowProvider>
        <FlowEditNavBar handleNewNode={handleNewNodeToWorkspace} />
        <NodeConfigrationNavigation
          isOpen={configurationPanel.isOpen}
          parentNode={configurationPanel.parentNode}
          childNode={configurationPanel.childNode}
          handleSave={configurationPanel.handleSave}
          handleDelete={configurationPanel.handleDelete}
          handleClose={configurationPanel.handleClose}
        />
        <FlowEditActionBar
          flowName={flowName}
          flowLastUpdatedAt={chargerFlow.updatedAt}
          isLoading={false}
          isDisabled={false}
          onFlowNameChange={setFlowName}
          onFlowSave={handleSaveFlow}
        />
        <ReactFlow
          className={s.reactFlow}
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          panOnScroll
          selectionOnDrag
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onInit={(instance) => instance.fitView()}>
          <MiniMap />
          <Controls />
          <Background />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
};

function FlowEdit({ chargerFlow }: FlowEditProps) {
  return (
    <div className={s.container}>
      <ReactFlowProvider>
        <ReactFlowBase chargerFlow={chargerFlow} />
      </ReactFlowProvider>
    </div>
  );
}

export default FlowEdit;
